import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { ulid } from 'ulid';
import API from '../../../Services/API';
import { useSession } from '../../../Components/Session';
import { useToasts } from '../../../Components/Toasts';
import { ChallengeTypes } from '../../../Data/Users';
import { isLocal } from '../../../Services/helpers';
import EmailChallenge from './EmailChallenge';
import PasswordChallenge from './PasswordChallenge';
import CompromisedPasswordChallenge from './CompromisedPasswordChallenge';
import TrustDeviceChallenge from './TrustDeviceChallenge';
import MfaChallenge from './MfaChallenge';
import PasskeyChallenge from './PasskeyChallenge';
import RegisterChallenge from './RegisterChallenge';
import Problems from './Problems';
import { PageLoading } from '../../../Components/Loading';
import Card from '../../../Components/Containers/Card';
import Button from '../../../Components/Button/Button';
import UpdatePrompt from '../../../Components/UpdatePrompt';

const Challenges = () => {
    const {setUser, setBusiness, fetchDetails, deviceId} = useSession();
    const navigate = useNavigate();
    const location = useLocation();
    const {showError} = useToasts();
    const [sessionId, setSessionId] = useState(() => ulid());
    const [challenge, setChallenge] = useState(null);
    const [isStarting, setIsStarting] = useState(true);
    const [email, setEmail] = useState(null);
    const [showProblems, setShowProblems] = useState(false);
    const [expiresAt, setExpiresAt] = useState(null);
    const [isExpired, setIsExpired] = useState(false);
    const [registerMode, setRegisterMode] = useState(false);
    const [registerEmail, setRegisterEmail] = useState(null);
    const hasStarted = useRef(false);

    const startChallenge = async () => {
        const newSessionId = ulid();
        setSessionId(newSessionId);
        setIsStarting(true);
        setEmail(null);
        setIsExpired(false);
        setRegisterMode(false);
        setRegisterEmail(null);
        try {
            const payload = {
                sessionId: newSessionId,
                deviceId,
            };

            if (isLocal()) {
                payload._ja4Fingerprint = 't13d0016h2_8daaf6152771_0d0000000000';
                payload._city = 'London';
            }

            const response = await API.post('/auth/challenge/start', payload);
            setChallenge(response.challenge);
            setIsExpired(false);
            setExpiresAt(response.expiresAt ? new Date(response.expiresAt) : null);
        } catch (err) {
            showError(API.parseErrorResponse(err) ?? 'Failed to start authentication');
        } finally {
            setIsStarting(false);
        }
    };

    const handleChallengeResponse = async (data) => {
        if (!sessionId || !challenge) {
            return;
        }

        try {
            const payload = {
                sessionId,
                type: challenge.type,
                data,
            };

            if (isLocal()) {
                payload._ja4Fingerprint = 't13d0016h2_8daaf6152771_0d0000000000';
                payload._city = 'London';
            }

            const response = await API.post('/auth/challenge/respond', payload);

            if (data?.email && challenge.type === ChallengeTypes.EMAIL) {
                setEmail(data.email);
            }

            if (response.complete) {
                if (response.token) {
                    API.setToken(response.token);
                    setUser(response.user);
                    setBusiness(response.business);
                    fetchDetails();
                    const redirectAfterLogin = window.sessionStorage.getItem('redirectAfterLogin');
                    window.sessionStorage.removeItem('redirectAfterLogin');
                    navigate(redirectAfterLogin || '/');
                } else {
                    showError('Authentication completed but no token received');
                }
            } else if (response.challenge) {
                setChallenge(response.challenge);
                setExpiresAt(response.expiresAt ? new Date(response.expiresAt) : null);
            } else {
                showError('Unexpected response from server');
            }
        } catch (err) {
            if (challenge.type === ChallengeTypes.EMAIL && data?.email) {
                setRegisterMode(true);
                setRegisterEmail(data.email);
            } else {
                showError(API.parseErrorResponse(err) ?? 'Authentication failed');
            }
        }
    };

    useEffect(() => {
        if (hasStarted.current) {
            return;
        }

        hasStarted.current = true;
        startChallenge();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (!expiresAt) {
            return;
        }

        const checkExpiration = () => {
            const now = new Date();
            if (now >= expiresAt) {
                setIsExpired(true);
            }
        };

        checkExpiration();

        const interval = setInterval(checkExpiration, 5000);

        return () => clearInterval(interval);
    }, [expiresAt]);

    const renderChallenge = () => {
        if (registerMode) {
            return (
                <>
                    {registerEmail && <div className="mb-4 p-3 surface-2 rounded-lg text-sm">
                        <p className="mb-3">
                            We couldn't find an account for <strong>{registerEmail}</strong>.
                            Double-check the address, or set up your business in a couple of minutes.
                        </p>
                        <Button
                            label="Set up your business"
                            theme="primary"
                            onClick={() => navigate('/register?email=' + encodeURIComponent(registerEmail))}
                        />
                    </div>}
                    <RegisterChallenge
                        email={registerEmail}
                        onBack={() => {
                            setRegisterMode(false);
                            setRegisterEmail(null);
                        }}
                        onSuccess={(registeredEmail) => {
                            setRegisterMode(false);
                            setRegisterEmail(null);
                            handleChallengeResponse({email: registeredEmail});
                        }}
                    />
                    <button
                        onClick={() => {
                            setRegisterMode(false);
                            setRegisterEmail(null);
                            startChallenge();
                        }}
                        className="mt-2 text-sm text-muted hover:text-brand cursor-pointer"
                        type="button"
                    >
                        Back to login
                    </button>
                </>
            );
        }

        if (isStarting || !challenge) {
            return <PageLoading />;
        }

        if (isExpired) {
            return (
                <div className="text-center py-8">
                    <p className="text-muted mb-4">Your login session has expired.</p>
                    <Button
                        label="Start Again"
                        theme="primary"
                        onClick={startChallenge}
                    />
                </div>
            );
        }

        switch (challenge.type) {
            case ChallengeTypes.EMAIL:
                return <EmailChallenge email={location.state?.email} onSubmit={handleChallengeResponse} />;
            case ChallengeTypes.PASSWORD:
                return <PasswordChallenge onSubmit={handleChallengeResponse} />;
            case ChallengeTypes.COMPROMISED_PASSWORD:
                return <CompromisedPasswordChallenge onSubmit={handleChallengeResponse} />;
            case ChallengeTypes.TRUST_DEVICE:
                return <TrustDeviceChallenge onSubmit={handleChallengeResponse} />;
            case ChallengeTypes.MFA:
                return <MfaChallenge onSubmit={handleChallengeResponse} />;
            case ChallengeTypes.PASSKEY:
                return <PasskeyChallenge onSubmit={handleChallengeResponse} challenge={challenge} />;
            default:
                return <div>Unknown challenge type: {challenge.type}</div>;
        }
    };

    if (showProblems) {
        return <Problems onBack={() => setShowProblems(false)} />;
    }

    return <>
        <img src="/logo-name-horizontal.png"
            alt="getSoapy Logo"
            width="888"
            height="183"
            className="w-[75%] h-auto mx-auto mb-8"
        />

        <Card>
            {renderChallenge()}
        </Card>

        <div className="mt-5 flex flex-col gap-y-2 text-sm text-center">
            <Link to="/register" className="text-muted hover:text-brand">
                New to getSoapy? <span className="font-medium">Create an account</span>
            </Link>
            {email && <button
                onClick={startChallenge}
                className="cursor-pointer text-muted hover:text-brand"
                type="button"
            >
                Not {email}?
            </button>}
            <button
                onClick={() => setShowProblems(true)}
                className="cursor-pointer text-muted hover:text-brand"
                type="button"
            >
                Problems logging in?
            </button>
        </div>

        <UpdatePrompt />
    </>;
};

export default Challenges;
